// @ts-nocheck
import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Code,
  Divider,
  Alert,
  ThemeIcon,
  SimpleGrid,
} from '@/components/ui';
import { IconBolt, IconInfoCircle } from '@tabler/icons-react';
import {
  PresenceIndicator,
  PresenceDot,
  OnlineUsersList,
  OnlineUsersCount,
} from '@/components/presence';

/**
 * CCMS Presence Components — documented stub showcase.
 *
 * Every component in `@/components/presence` calls the SDK PresenceHub on mount
 * (`usePresence()` / `useCCMS()`) and fetches live presence data inside a
 * `useEffect`. That means they cannot be rendered safely on a static docs page
 * without a configured Presence WebSocket hub + CCMS provider — doing so would
 * throw at render. So this page DOCUMENTS each component (name, purpose, key
 * props, usage snippet, what it needs to run live) and intentionally renders
 * NONE of them live.
 *
 * The imports above are kept so this page also acts as a compile-time check that
 * the public names are still exported from the barrel.
 */
export default function CcmsPresencePage() {
  // Touch the imports so linters/bundlers keep them as an export-surface check.
  // They are NOT rendered — each requires the live Presence WebSocket hub.
  const _exportSurfaceCheck = {
    PresenceIndicator,
    PresenceDot,
    OnlineUsersList,
    OnlineUsersCount,
  };
  void _exportSurfaceCheck;

  const LIVE_REQUIREMENT =
    'Presence WebSocket hub for live data (Indicator/Dot render with explicit props)';

  return (
    <Stack gap="xl">
      {/* ---------------------------------------------------------------- */}
      {/* Header                                                           */}
      {/* ---------------------------------------------------------------- */}
      <Stack gap="xs">
        <Title order={2}>Presence Components (Indicator, Dot, OnlineUsersList)</Title>
        <Text c="dimmed" size="sm">
          Real-time user presence widgets backed by the CCMS SDK PresenceHub. Each
          component subscribes to live presence over a WebSocket on mount, so they
          are documented here rather than rendered live.
        </Text>
      </Stack>

      <Alert
        variant="light"
        color="blue"
        icon={<IconInfoCircle size={18} />}
        title="Why these render as docs only"
      >
        <Text size="sm">
          Every presence component below calls an SDK hook (<Code>usePresence()</Code>
          {' '}and/or <Code>useCCMS()</Code>) on mount and fetches presence asynchronously
          from the PresenceHub. Without a configured Presence WebSocket hub and the CCMS
          provider in the tree, mounting them would throw. To keep this showcase
          crash-free we render no live previews — wire up the hub in a real app screen
          to see them in action.
        </Text>
      </Alert>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        {/* -------------------------------------------------------------- */}
        {/* PresenceIndicator                                              */}
        {/* -------------------------------------------------------------- */}
        <Card withBorder p="lg">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={4}>
                <Title order={4}>PresenceIndicator</Title>
                <Text size="sm" c="dimmed">
                  Colored status badge/dot showing a single user&apos;s live presence
                  (online / away / busy / offline) with optional label and tooltip.
                </Text>
              </Stack>
              <ThemeIcon variant="light" color="green" size="lg" radius="md">
                <IconBolt size={18} />
              </ThemeIcon>
            </Group>

            <Divider label="Key props" labelPosition="left" />
            <Stack gap={2}>
              <Text size="sm"><Code>userId</Code> — string — required</Text>
              <Text size="sm">
                <Code>size</Code> — 'xs' | 'sm' | 'md' | 'lg' — optional (default 'sm')
              </Text>
              <Text size="sm"><Code>showLabel</Code> — boolean — optional (default false)</Text>
              <Text size="sm"><Code>label</Code> — string — optional (overrides status text)</Text>
              <Text size="sm">
                <Code>showTooltip</Code> — boolean — optional (default true)
              </Text>
            </Stack>

            <Divider label="Usage" labelPosition="left" />
            <Code block language="tsx">
{`import { PresenceIndicator } from '@/components/presence';

<PresenceIndicator userId="123" size="sm" showLabel />`}
            </Code>

            <Alert variant="light" color="orange" icon={<IconBolt size={16} />}>
              <Text size="xs">Needs to run live: {LIVE_REQUIREMENT}</Text>
            </Alert>
          </Stack>
        </Card>

        {/* -------------------------------------------------------------- */}
        {/* PresenceDot                                                    */}
        {/* -------------------------------------------------------------- */}
        <Card withBorder p="lg">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={4}>
                <Title order={4}>PresenceDot</Title>
                <Text size="sm" c="dimmed">
                  Minimal colored dot (no label, no tooltip) reflecting a user&apos;s
                  live presence — ideal for avatar overlays and dense lists.
                </Text>
              </Stack>
              <ThemeIcon variant="light" color="teal" size="lg" radius="md">
                <IconBolt size={18} />
              </ThemeIcon>
            </Group>

            <Divider label="Key props" labelPosition="left" />
            <Stack gap={2}>
              <Text size="sm"><Code>userId</Code> — string — required</Text>
              <Text size="sm">
                <Code>size</Code> — number (px) — optional (default 10)
              </Text>
            </Stack>

            <Divider label="Usage" labelPosition="left" />
            <Code block language="tsx">
{`import { PresenceDot } from '@/components/presence';

<PresenceDot userId="123" size={12} />`}
            </Code>

            <Alert variant="light" color="orange" icon={<IconBolt size={16} />}>
              <Text size="xs">Needs to run live: {LIVE_REQUIREMENT}</Text>
            </Alert>
          </Stack>
        </Card>

        {/* -------------------------------------------------------------- */}
        {/* OnlineUsersList                                                */}
        {/* -------------------------------------------------------------- */}
        <Card withBorder p="lg">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={4}>
                <Title order={4}>OnlineUsersList</Title>
                <Text size="sm" c="dimmed">
                  Scrollable list of currently online users with avatar, presence dot,
                  status badge, optional search and status filtering, updating live via
                  SignalR.
                </Text>
              </Stack>
              <ThemeIcon variant="light" color="indigo" size="lg" radius="md">
                <IconBolt size={18} />
              </ThemeIcon>
            </Group>

            <Divider label="Key props" labelPosition="left" />
            <Stack gap={2}>
              <Text size="sm"><Code>maxUsers</Code> — number — optional</Text>
              <Text size="sm"><Code>showSearch</Code> — boolean — optional (default false)</Text>
              <Text size="sm">
                <Code>statusFilter</Code> — 'online' | 'away' | 'busy' | 'all' — optional
                (default 'all')
              </Text>
              <Text size="sm">
                <Code>onUserClick</Code> — (userId: string) =&gt; void — optional
              </Text>
              <Text size="sm">
                <Code>height</Code> — number | string — optional (default 400)
              </Text>
            </Stack>

            <Divider label="Usage" labelPosition="left" />
            <Code block language="tsx">
{`import { OnlineUsersList } from '@/components/presence';

<OnlineUsersList
  maxUsers={10}
  showSearch
  statusFilter="online"
  onUserClick={(userId) => navigate(\`/users/\${userId}\`)}
/>`}
            </Code>

            <Alert variant="light" color="orange" icon={<IconBolt size={16} />}>
              <Text size="xs">Needs to run live: {LIVE_REQUIREMENT}</Text>
            </Alert>
          </Stack>
        </Card>

        {/* -------------------------------------------------------------- */}
        {/* OnlineUsersCount                                               */}
        {/* -------------------------------------------------------------- */}
        <Card withBorder p="lg">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={4}>
                <Title order={4}>OnlineUsersCount</Title>
                <Text size="sm" c="dimmed">
                  Compact green badge showing how many users are currently online,
                  incrementing/decrementing live as users connect and disconnect.
                </Text>
              </Stack>
              <ThemeIcon variant="light" color="green" size="lg" radius="md">
                <IconBolt size={18} />
              </ThemeIcon>
            </Group>

            <Divider label="Key props" labelPosition="left" />
            <Stack gap={2}>
              <Text size="sm">
                <Badge size="xs" variant="light" color="gray">no props</Badge>
              </Text>
              <Text size="sm" c="dimmed">
                Self-contained: reads the online count straight from the PresenceHub.
              </Text>
            </Stack>

            <Divider label="Usage" labelPosition="left" />
            <Code block language="tsx">
{`import { OnlineUsersCount } from '@/components/presence';

<OnlineUsersCount />`}
            </Code>

            <Alert variant="light" color="orange" icon={<IconBolt size={16} />}>
              <Text size="xs">Needs to run live: {LIVE_REQUIREMENT}</Text>
            </Alert>
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
